import { isEmpty, isNil } from 'lodash';
import React from 'react';
import { MBView } from '@shared/components/MBView';
import { MBIconV2Name } from '@shared/components/image';
import { PressableTextRow } from '@shared/components/list';
import { InputComponent } from '@shared/scenes/workflows/InputComponent';
import {
  hasSelectableDefault,
  isAuditTagComponent,
  isPersonComponent,
  isRelatedCardComponent,
  isSubformComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { cursorPointerStyle } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { DefaultPersonRow } from './DefaultPersonRow';
import { DefaultRelatedCardRow } from './DefaultRelatedCardRow';
import { DefaultSubformRow } from './DefaultSubformRow';
import { DefaultTagRow } from './DefaultTagRow';
import { DefaultTextRow } from './DefaultTextRow';

type Component = MBAPI.WorkflowTemplate_components;

interface Props {
  component: InputComponent;
  overridePath?: string;
}

export function ComponentDefaultItemRow({ component, overridePath }: Props) {
  const hasSelectableDefaultValue = hasSelectableDefault(component);
  const hasDefaultValue = hasDefault(component, overridePath);
  if (hasSelectableDefaultValue && !hasDefaultValue) {
    return <NoDefaultRow icon={component.icon as MBIconV2Name} />;
  }
  return <DefaultRow {...{ component, overridePath }} />;
}

function hasDefault(component: Component, overridePath?: string): boolean {
  if (isTagComponent(component) && isNil(overridePath)) {
    return !isEmpty(component.default?.value);
  }
  if (isTagComponent(component) && !isNil(overridePath)) {
    return !isEmpty(component[overridePath]?.value);
  }
  if (isAuditTagComponent(component)) {
    return !isEmpty(component.default?.value);
  }
  if (isPersonComponent(component)) {
    return !isEmpty(component.default?.userIds) || !isEmpty(component.default?.token);
  }
  if (isRelatedCardComponent(component)) {
    return !isEmpty(component.default?.cardIds);
  }
  if (isSubformComponent(component)) {
    return !isEmpty(component.default?.workflowTemplateId);
  }
  return false;
}

function DefaultRow({ component, overridePath }: { component: Component; overridePath?: string }) {
  if (isTagComponent(component)) {
    return <DefaultTagRow component={component} overridePath={overridePath} />;
  }
  if (isAuditTagComponent(component)) {
    return <DefaultTagRow component={component} overridePath={overridePath} />;
  }
  if (isPersonComponent(component)) {
    return <DefaultPersonRow component={component} />;
  }
  if (isRelatedCardComponent(component)) {
    return <DefaultRelatedCardRow component={component} />;
  }
  if (isSubformComponent(component)) {
    return <DefaultSubformRow component={component} />;
  }
  if (isTextComponent(component)) {
    return <DefaultTextRow component={component} />;
  }
  return null;
}

function NoDefaultRow({ icon }: { icon: MBIconV2Name }) {
  return (
    <MBView style={cursorPointerStyle}>
      <PressableTextRow
        text={i18n('builder.defaultRow.noDefault')}
        leftIcon={icon}
        rightIcon="arrow-point-right"
        enabled={true}
      />
    </MBView>
  );
}
